/**
 * @module engine3d/models/process
 * @summary 3D models for the Processing and Dies & Heads families — extruders,
 *   kneaders, melt pumps, screen changers and extrusion heads.
 * @description
 *   These are the machines the whole line is named after, so they get the most
 *   detail: a barrel with individually modelled heater/cooling zones, a feed
 *   throat, a gearbox-and-motor drive train, a control cabinet, and a proper
 *   flanged adapter at the discharge end for the die to bolt onto.
 */
import {
  box,
  boxCentered,
  castors,
  controlBox,
  cylinder,
  cylinderX,
  cylinderZ,
  flange,
  frustum,
  gearbox,
  heaterBands,
  hmi,
  merge,
  motorUnit,
  p,
  pm,
  rectFoot,
  revolve,
  rotateX,
  rotateZ,
  screwFlight,
  skid,
  sphere,
  translate,
  v3,
  type Builder,
  type Mesh,
  type Model3,
} from './kit';

/**
 * The extruder archetype: barrel along +X on a skid base, fed from a throat
 * near the -X end, driven through a gearbox at that end, discharging at +X.
 *
 * @param barrels  1 for a single screw, 2 for the intermeshing twin-screw pair.
 * @param zones    Number of heater/cooling zones along the barrel.
 * @param vent     Draw a vacuum vent stack (devolatilising machines).
 */
function extruder(barrels: 1 | 2, zones: number, vent: boolean): Builder {
  return ({ w, h, d }): Model3 => {
    const baseH = h * 0.34;
    const R = Math.min(h * 0.28, d * 0.22);
    const cy = baseH + R * 1.5;
    const barrelLen = w * 0.6;
    const bx = w * 0.1; // barrel centre, biased toward the discharge end
    const off = barrels === 2 ? R * 0.62 : 0;
    const barrelMesh = (z: number): Mesh => translate(cylinderX(R, barrelLen, 18), bx, cy, z);
    const barrel =
      barrels === 2 ? merge(barrelMesh(off), barrelMesh(-off)) : barrelMesh(0);
    const bands =
      barrels === 2
        ? merge(
            translate(heaterBands(R, barrelLen * 0.92, zones), bx, cy, off),
            translate(heaterBands(R, barrelLen * 0.92, zones), bx, cy, -off),
          )
        : translate(heaterBands(R, barrelLen * 0.92, zones), bx, cy, 0);
    const cab = controlBox(w * 0.16, h * 0.72, d * 0.4);
    const mu = motorUnit(w * 0.26, R * 0.95);
    const throatX = bx - barrelLen * 0.4;
    return {
      parts: [
        p(skid(w * 0.98, d * 0.96, baseH * 0.22), 'galv'),
        // Barrel support pedestals.
        p(
          merge(
            translate(boxCentered(w * 0.08, baseH, d * 0.6), bx + barrelLen * 0.32, baseH / 2, 0),
            translate(boxCentered(w * 0.08, baseH, d * 0.6), bx - barrelLen * 0.28, baseH / 2, 0),
          ),
          'machine',
        ),
        p(barrel, 'machineDark'),
        p(bands, 'hot'),
        // Screws, visible where the throat opens into the barrel.
        p(
          barrels === 2
            ? merge(
                translate(screwFlight(R * 0.72, barrelLen, 7, R * 0.3), bx, cy, off),
                translate(screwFlight(R * 0.72, barrelLen, 7, R * 0.3), bx, cy, -off),
              )
            : translate(screwFlight(R * 0.78, barrelLen, 7, R * 0.34), bx, cy, 0),
          'steel',
        ),
        // Feed throat + hopper adapter.
        pm(
          'stainless',
          translate(box(R * 2, R * 1.5, R * 2.4), throatX, cy + R * 0.6, 0),
          translate(frustum(R * 0.9, R * 1.25, R * 1.1, { seg: 14 }), throatX, cy + R * 2.1, 0),
          translate(flange(R * 1.35, 1.6, 14), throatX, cy + R * 3.1, 0),
        ),
        // Drive train at the -X end.
        p(translate(gearbox(w * 0.14, R * 3, d * 0.62), bx - barrelLen * 0.5 - w * 0.07, baseH * 0.4, 0), 'cast'),
        p(translate(merge(mu.body, mu.fins), bx - barrelLen * 0.5 - w * 0.2, cy - R * 0.3, 0), 'motor'),
        // Discharge adapter the die bolts onto.
        pm(
          'machineDark',
          translate(cylinderX(R * 0.85, w * 0.1, 14), bx + barrelLen * 0.5 + w * 0.05, cy, 0),
          translate(rotateZ(flange(R * 1.3, 2, 14), -Math.PI / 2), bx + barrelLen * 0.5 + w * 0.1, cy, 0),
        ),
        // Operator side: cabinet + HMI.
        p(translate(cab.body, -w * 0.36, 0, -d * 0.42), 'panel'),
        p(translate(cab.face, -w * 0.36, 0, -d * 0.42), 'screen'),
        p(translate(hmi(w * 0.1, h * 0.3).arm, -w * 0.1, baseH, -d * 0.46), 'machine'),
        p(translate(hmi(w * 0.1, h * 0.3).screen, -w * 0.1, baseH, -d * 0.46), 'screen'),
        ...(vent
          ? [
              p(
                merge(
                  translate(cylinder(R * 0.5, R * 2.2, { seg: 12 }), bx + barrelLen * 0.1, cy + R, 0),
                  translate(flange(R * 0.7, 1.6, 12), bx + barrelLen * 0.1, cy + R * 3.2, 0),
                ),
                'stainless',
              ),
            ]
          : []),
      ],
      height: cy + R * 3.4,
      ports: {
        N: { p: v3(throatX, cy + R * 3.2, 0), n: v3(0, 1, 0) },
        W: { p: v3(-w / 2, cy, 0), n: v3(-1, 0, 0) },
        E: { p: v3(bx + barrelLen * 0.5 + w * 0.12, cy, 0), n: v3(1, 0, 0) },
        S: { p: v3(bx, baseH * 0.2, 0), n: v3(0, -1, 0) },
      },
      footprint: rectFoot(w, d),
    };
  };
}

export const tse = extruder(2, 8, true);
export const sse = extruder(1, 6, false);

/** BUSS co-kneader — a single reciprocating screw with kneading bolt rows. */
export const coknead: Builder = ({ w, h, d }): Model3 => {
  const base = extruder(1, 5, false)({ w, h, d });
  const R = Math.min(h * 0.28, d * 0.22);
  const cy = h * 0.34 + R * 1.5;
  const bolts: Mesh[] = [];
  for (let i = 0; i < 5; i++) {
    const x = w * 0.1 - w * 0.24 + (w * 0.48 * i) / 4;
    for (const a of [0, 2.1, 4.2])
      bolts.push(translate(rotateZ(rotateX(cylinder(R * 0.16, R * 0.5, { seg: 8 }), a), 0), x, cy + R * Math.cos(a) * 0.9, R * Math.sin(a) * 0.9));
  }
  return {
    ...base,
    parts: [...base.parts, p(merge(...bolts), 'steel')],
  };
};

/** Melt / gear pump — twin gears in a heated cast body with a small drive. */
export const gearpump: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, h) * 0.3;
  const cy = h * 0.5;
  return {
    parts: [
      p(translate(boxCentered(w * 0.3, h * 0.5, d * 0.3), 0, h * 0.16, 0), 'machine'),
      p(
        merge(
          translate(cylinderZ(R, d * 0.6, 18), 0, cy + R * 0.42, -d * 0.3),
          translate(cylinderZ(R, d * 0.6, 18), 0, cy - R * 0.42, -d * 0.3),
          translate(box(R * 2.1, R * 1.4, d * 0.6), 0, cy - R * 0.7, 0),
        ),
        'cast',
      ),
      p(translate(cylinderZ(R * 1.16, 2.4, 18), 0, cy, d * 0.3), 'hot'),
      pm(
        'machineDark',
        translate(rotateZ(flange(R * 0.62, 2, 12), Math.PI / 2), -R * 1.2, cy, 0),
        translate(rotateZ(flange(R * 0.62, 2, 12), -Math.PI / 2), R * 1.2, cy, 0),
        translate(cylinderX(R * 0.36, R * 0.9, 12), -R * 1.3, cy, 0),
        translate(cylinderX(R * 0.36, R * 0.9, 12), R * 1.3, cy, 0),
      ),
      p(translate(merge(motorUnit(w * 0.4, R * 0.34).body, motorUnit(w * 0.4, R * 0.34).fins), 0, cy + R * 0.42, -d * 0.62), 'motor'),
    ],
    height: cy + R * 1.3,
    ports: {
      W: { p: v3(-R * 1.75, cy, 0), n: v3(-1, 0, 0) },
      E: { p: v3(R * 1.75, cy, 0), n: v3(1, 0, 0) },
      N: { p: v3(0, cy + R * 1.3, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, cy - R * 1.4, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w, d),
  };
};

/** Screen changer — a slide-plate body with the hydraulic ram alongside. */
export const screenchange: Builder = ({ w, h, d }): Model3 => {
  const cy = h * 0.5;
  const r = Math.min(w, d) * 0.3;
  return {
    parts: [
      p(translate(box(w * 0.62, h * 0.6, d * 0.8), 0, cy - h * 0.3, 0), 'machineDark'),
      p(translate(box(w * 1.5, h * 0.16, d * 0.5), 0, cy - h * 0.08, 0), 'steel'),
      p(translate(cylinderZ(r * 1.3, 2.4, 14), 0, cy, d * 0.4), 'hot'),
      pm(
        'machineDark',
        translate(cylinderX(r * 0.7, w * 0.36, 12), -w * 0.4, cy, 0),
        translate(cylinderX(r * 0.7, w * 0.36, 12), w * 0.4, cy, 0),
        translate(rotateZ(flange(r, 2, 12), Math.PI / 2), -w * 0.55, cy, 0),
        translate(rotateZ(flange(r, 2, 12), -Math.PI / 2), w * 0.55, cy, 0),
      ),
      // Hydraulic ram + power pack.
      p(translate(cylinderZ(r * 0.5, d * 0.7, 12), 0, cy + h * 0.24, -d * 0.75), 'accent'),
      p(translate(box(w * 0.5, h * 0.2, d * 0.3), 0, cy + h * 0.34, -d * 0.9), 'machine'),
      p(translate(cylinder(r * 0.3, h * 0.5, { seg: 10 }), 0, 0, 0), 'galv'),
    ],
    height: cy + h * 0.5,
    ports: {
      W: { p: v3(-w * 0.58, cy, 0), n: v3(-1, 0, 0) },
      E: { p: v3(w * 0.58, cy, 0), n: v3(1, 0, 0) },
      N: { p: v3(0, cy + h * 0.5, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, 0, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w * 1.1, d),
  };
};

/** Static mixer — a flanged spool with the helical elements shown inside. */
export const staticmix: Builder = ({ w, h, d }): Model3 => {
  const r = Math.min(h, d) * 0.34;
  const cy = h * 0.5;
  const elems: Mesh[] = [];
  for (let i = 0; i < 5; i++)
    elems.push(
      translate(rotateX(boxCentered(w * 0.13, r * 1.7, 1.2), (i * Math.PI) / 2.4), -w * 0.28 + (w * 0.56 * i) / 4, cy, 0),
    );
  return {
    parts: [
      p(translate(cylinderX(r, w * 0.72, 16), 0, cy, 0), 'stainless'),
      p(merge(...elems), 'steel'),
      pm(
        'machine',
        translate(rotateZ(flange(r * 1.5, 2, 14), Math.PI / 2), -w * 0.38, cy, 0),
        translate(rotateZ(flange(r * 1.5, 2, 14), -Math.PI / 2), w * 0.38, cy, 0),
      ),
      p(translate(cylinder(r * 0.4, cy - r, { seg: 8 }), 0, 0, 0), 'galv'),
    ],
    height: cy + r,
    ports: {
      W: { p: v3(-w * 0.4, cy, 0), n: v3(-1, 0, 0) },
      E: { p: v3(w * 0.4, cy, 0), n: v3(1, 0, 0) },
      N: { p: v3(0, cy + r, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, 0, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w * 0.9, d),
  };
};

/**
 * Pellet scanner — a bench optical sorter, modelled on the machine itself: a
 * white enclosed cabinet on castors with a blue plinth stripe, an inclined
 * discharge chute and sample tray out of the left side over a collection bin,
 * and a big HMI cantilevered off the right on its own arm.
 *
 * Enclosed, unlike the free-fall column it replaces: on this class of machine
 * the optics live behind the access doors, and what you actually recognise it
 * by is the cabinet, the chute, the screen and the beacon on top.
 */
export const pelletscanner: Builder = ({ w, h, d }): Model3 => {
  const wheelR = 2.4;
  const floorY = wheelR * 2;
  const cw = w * 0.6;
  const cd = d * 0.7;
  const cabX = w * 0.08; // cabinet sits right of centre; the chute takes the left
  const cabH = h * 0.58;
  const cabTop = floorY + cabH;
  const chuteTopY = floorY + cabH * 0.62;
  const chuteBotY = h * 0.16;
  const chuteX = -w * 0.34;
  const binY = 0;
  const hmiX = cabX + cw / 2 + w * 0.12;
  const top = cabTop + h * 0.115;

  // Inclined discharge chute: a floor plate with a wall each side.
  const chute = (t: number, ww: number): Mesh =>
    rotateZ(box(h * 0.46, t, ww), -0.72);

  return {
    parts: [
      p(castors(cw * 0.86, cd * 0.86, wheelR), 'rubber'),
      p(translate(box(cw * 0.9, floorY, cd * 0.9), cabX, 0, 0), 'machine'),
      // ── Cabinet: white panels, blue plinth stripe and logo band ──
      p(translate(box(cw, cabH, cd), cabX, floorY, 0), 'plastic'),
      pm(
        'painted',
        translate(box(cw * 1.03, h * 0.045, cd * 1.03), cabX, floorY, 0),
        translate(box(cw * 0.46, h * 0.05, 0.8), cabX, floorY + cabH * 0.2, cd / 2 + 0.4),
      ),
      p(translate(box(cw * 1.04, 2.2, cd * 1.04), cabX, cabTop - 2.2, 0), 'plastic'),
      // Two access doors on the front, with their handles.
      pm(
        'lagging',
        ...[-1, 1].map((sx) =>
          translate(
            box(cw * 0.38, cabH * 0.44, 1),
            cabX + sx * cw * 0.22,
            floorY + cabH * 0.42,
            cd / 2 + 0.5,
          ),
        ),
      ),
      pm(
        'panel',
        ...[-1, 1].map((sx) =>
          translate(
            box(cw * 0.1, 2, 1.6),
            cabX + sx * cw * 0.22,
            floorY + cabH * 0.78,
            cd / 2 + 1.2,
          ),
        ),
      ),
      // ── Discharge chute and sample tray out of the left side ──
      pm(
        'plastic',
        translate(chute(2, cd * 0.42), chuteX, chuteTopY, 0),
        translate(rotateZ(box(h * 0.46, cd * 0.16, 1.8), -0.72), chuteX, chuteTopY, -cd * 0.21),
        translate(rotateZ(box(h * 0.46, cd * 0.16, 1.8), -0.72), chuteX, chuteTopY, cd * 0.21),
      ),
      pm(
        'stainless',
        // The flat sample tray the product lands on, tipped a few degrees.
        translate(rotateZ(box(w * 0.22, 1.6, cd * 0.4), -0.2), -w * 0.44, chuteBotY, 0),
        translate(box(w * 0.22, h * 0.035, 1.4), -w * 0.44, chuteBotY, -cd * 0.2),
        translate(box(w * 0.22, h * 0.035, 1.4), -w * 0.44, chuteBotY, cd * 0.2),
      ),
      // Collection bin on the floor under the tray.
      p(translate(box(w * 0.19, h * 0.13, d * 0.24), -w * 0.4, binY, 0), 'rubber'),
      // Tower light on a post beside the chute.
      p(translate(cylinder(0.9, h * 0.3, { seg: 8 }), chuteX - w * 0.06, chuteTopY, -cd * 0.3), 'galv'),
      p(translate(cylinder(2, h * 0.035, { seg: 10 }), chuteX - w * 0.06, chuteTopY + h * 0.3, -cd * 0.3), 'accent'),
      p(translate(cylinder(2, h * 0.035, { seg: 10 }), chuteX - w * 0.06, chuteTopY + h * 0.265, -cd * 0.3), 'safety'),
      p(translate(cylinder(2, h * 0.035, { seg: 10 }), chuteX - w * 0.06, chuteTopY + h * 0.23, -cd * 0.3), 'motor'),
      // ── HMI on its arm, off the right-hand side ──
      p(translate(cylinderX(1.8, w * 0.12, 10), cabX + cw / 2, floorY + cabH * 0.62, 0), 'machine'),
      p(translate(box(w * 0.17, h * 0.34, d * 0.4), hmiX, floorY + cabH * 0.36, 0), 'machine'),
      p(translate(box(w * 0.13, h * 0.18, 1.2), hmiX, floorY + cabH * 0.52, d * 0.2 + 0.6), 'panel'),
      p(translate(box(w * 0.12, h * 0.07, 1), hmiX, floorY + cabH * 0.4, d * 0.2 + 0.6), 'lagging'),
      // Beacon on the cabinet roof.
      pm(
        'accent',
        translate(cylinder(w * 0.035, h * 0.05, { seg: 12 }), cabX + cw * 0.3, cabTop, 0),
        translate(sphere(w * 0.035, 12, 6), cabX + cw * 0.3, cabTop + h * 0.05, 0),
      ),
    ],
    height: top,
    ports: {
      N: { p: v3(cabX, cabTop, -cd * 0.25), n: v3(0, 1, 0) },
      S: { p: v3(cabX, 0, 0), n: v3(0, -1, 0) },
      E: { p: v3(cabX + cw / 2, floorY + cabH * 0.24, 0), n: v3(1, 0, 0) },
      W: { p: v3(-w * 0.5, chuteBotY, 0), n: v3(-1, 0, 0) },
    },
    footprint: rectFoot(w, d * 0.86),
  };
};

// ─── Dies & heads ────────────────────────────────────────────────────────

/**
 * Crosshead die — the head the conductor passes through and the melt enters
 * from the side. Modelled with the conductor axis along +X and the melt inlet
 * arriving from -Z, which is how it sits on a cable line.
 */
export const crosshead: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, d) * 0.3;
  const cy = h * 0.58;
  return {
    parts: [
      p(translate(boxCentered(w * 0.3, cy, d * 0.4), 0, cy / 2, 0), 'machine'),
      // Head body: a stack of heated rings on the conductor axis.
      p(translate(cylinderX(R, w * 0.5, 18), 0, cy, 0), 'machineDark'),
      p(translate(heaterBands(R, w * 0.44, 3), 0, cy, 0), 'hot'),
      // Melt inlet arriving from the extruder behind.
      p(
        merge(
          translate(cylinderZ(R * 0.55, d * 0.55, 14), 0, cy, -d * 0.55),
          translate(rotateX(flange(R * 0.8, 2, 12), Math.PI / 2), 0, cy, -d * 0.55),
        ),
        'machineDark',
      ),
      // Die exit cone + tip.
      pm(
        'stainless',
        translate(rotateZ(frustum(R * 0.8, R * 0.3, w * 0.16, { seg: 16 }), -Math.PI / 2), w * 0.25, cy, 0),
        translate(cylinderX(R * 0.12, w * 0.16, 10), w * 0.48, cy, 0),
        translate(cylinderX(R * 0.12, w * 0.2, 10), -w * 0.34, cy, 0),
      ),
      // Adjusting bolts round the die face.
      p(
        merge(
          ...[0, 1, 2, 3].map((i) =>
            translate(
              cylinder(R * 0.1, R * 0.5, { seg: 6 }),
              Math.cos((i * Math.PI) / 2) * R * 0.7 * 0 + w * 0.2,
              cy + Math.cos((i * Math.PI) / 2) * R * 0.75,
              Math.sin((i * Math.PI) / 2) * R * 0.75,
            ),
          ),
        ),
        'brass',
      ),
    ],
    height: cy + R * 1.4,
    ports: {
      W: { p: v3(-w * 0.44, cy, 0), n: v3(-1, 0, 0) },
      E: { p: v3(w * 0.56, cy, 0), n: v3(1, 0, 0) },
      N: { p: v3(0, cy + R * 1.2, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, cy, -d * 0.56), n: v3(0, 0, -1) },
    },
    footprint: rectFoot(w * 0.8, d),
  };
};

/** Strand die — a heated block with a row of strand holes on its face. */
export const stranddie: Builder = ({ w, h, d }): Model3 => {
  const cy = h * 0.55;
  const holes: Mesh[] = [];
  for (let i = 0; i < 5; i++)
    holes.push(translate(cylinderX(Math.min(w, d) * 0.05, w * 0.3, 8), w * 0.3, cy, -d * 0.3 + (d * 0.6 * i) / 4));
  return {
    parts: [
      p(translate(boxCentered(w * 0.24, cy, d * 0.4), -w * 0.1, cy / 2, 0), 'machine'),
      p(translate(boxCentered(w * 0.5, h * 0.5, d * 0.9), 0, cy, 0), 'machineDark'),
      p(translate(boxCentered(w * 0.34, h * 0.56, d * 0.96), 0, cy, 0), 'hot'),
      p(merge(...holes), 'stainless'),
      pm(
        'machineDark',
        translate(cylinderX(Math.min(w, d) * 0.24, w * 0.3, 12), -w * 0.4, cy, 0),
        translate(rotateZ(flange(Math.min(w, d) * 0.36, 2, 12), Math.PI / 2), -w * 0.52, cy, 0),
      ),
    ],
    height: cy + h * 0.3,
    ports: {
      W: { p: v3(-w * 0.55, cy, 0), n: v3(-1, 0, 0) },
      E: { p: v3(w * 0.45, cy, 0), n: v3(1, 0, 0) },
      N: { p: v3(0, cy + h * 0.3, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, 0, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w * 0.6, d),
  };
};

/** Underwater die plate — a round heated plate with its cutting-chamber flange. */
export const uwdie: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, d) * 0.42;
  const cy = h * 0.55;
  return {
    parts: [
      p(translate(boxCentered(w * 0.28, cy, d * 0.4), 0, cy / 2, 0), 'machine'),
      p(translate(cylinderX(R, w * 0.3, 18), 0, cy, 0), 'machineDark'),
      p(translate(cylinderX(R * 1.08, w * 0.1, 18), 0, cy, 0), 'hot'),
      pm(
        'stainless',
        translate(rotateZ(flange(R * 1.2, 2.4, 18), -Math.PI / 2), w * 0.16, cy, 0),
        translate(cylinderX(R * 0.4, w * 0.3, 12), -w * 0.3, cy, 0),
      ),
      // The annulus of die holes.
      p(
        merge(
          ...Array.from({ length: 10 }, (_, i) => {
            const t = (i / 10) * Math.PI * 2;
            return translate(cylinderX(R * 0.07, w * 0.06, 6), w * 0.18, cy + Math.cos(t) * R * 0.6, Math.sin(t) * R * 0.6);
          }),
        ),
        'steel',
      ),
    ],
    height: cy + R * 1.25,
    ports: {
      W: { p: v3(-w * 0.44, cy, 0), n: v3(-1, 0, 0) },
      E: { p: v3(w * 0.2, cy, 0), n: v3(1, 0, 0) },
      N: { p: v3(0, cy + R * 1.25, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, 0, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w * 0.6, d),
  };
};

/** Flat sheet die — the wide coat-hanger die with its lip adjusting bolts. */
export const flatdie: Builder = ({ w, h, d }): Model3 => {
  const cy = h * 0.55;
  const bolts: Mesh[] = [];
  for (let i = 0; i < 7; i++)
    bolts.push(translate(cylinder(Math.min(w, d) * 0.05, h * 0.12, { seg: 6 }), w * 0.1, cy + h * 0.26, -d * 0.38 + (d * 0.76 * i) / 6));
  return {
    parts: [
      p(translate(boxCentered(w * 0.4, cy, d * 0.3), -w * 0.1, cy / 2, 0), 'machine'),
      p(translate(boxCentered(w * 0.7, h * 0.52, d * 0.94), 0, cy, 0), 'machineDark'),
      p(translate(boxCentered(w * 0.5, h * 0.58, d * 0.98), 0, cy, 0), 'hot'),
      p(merge(...bolts), 'brass'),
      p(translate(rotateZ(boxCentered(w * 0.3, h * 0.1, d * 0.94), 0.14), w * 0.4, cy - h * 0.2, 0), 'stainless'),
      pm(
        'machineDark',
        translate(cylinderX(Math.min(w, d) * 0.2, w * 0.4, 12), -w * 0.5, cy, 0),
        translate(rotateZ(flange(Math.min(w, d) * 0.3, 2, 12), Math.PI / 2), -w * 0.66, cy, 0),
      ),
    ],
    height: cy + h * 0.36,
    ports: {
      W: { p: v3(-w * 0.7, cy, 0), n: v3(-1, 0, 0) },
      E: { p: v3(w * 0.55, cy - h * 0.2, 0), n: v3(1, 0, 0) },
      N: { p: v3(0, cy + h * 0.36, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, 0, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w * 0.8, d),
  };
};

/** Degassing unit — a vacuum dome over a vented barrel section. */
export const degassing: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, d) * 0.3;
  const cy = h * 0.42;
  return {
    parts: [
      p(skid(w * 0.92, d * 0.92, h * 0.12), 'galv'),
      p(translate(cylinderX(R * 0.9, w * 0.9, 16), 0, cy, 0), 'machineDark'),
      p(translate(heaterBands(R * 0.9, w * 0.7, 3), 0, cy, 0), 'hot'),
      p(
        merge(
          translate(cylinder(R * 0.8, h * 0.3, { seg: 16 }), 0, cy + R * 0.7, 0),
          translate(revolve([{ x: R * 0.8, y: 0 }, { x: R * 0.6, y: h * 0.08 }, { x: 0, y: h * 0.14 }], 16, false), 0, cy + R * 0.7 + h * 0.3, 0),
        ),
        'stainless',
      ),
      p(translate(box(R * 0.7, h * 0.16, 1.4), 0, cy + R * 0.8, R * 0.81), 'glass'),
      pm(
        'pipe',
        translate(cylinder(R * 0.24, h * 0.18, { seg: 10 }), 0, cy + R * 0.7 + h * 0.44, 0),
        translate(cylinderZ(R * 0.2, d * 0.5, 10), 0, cy + R * 0.7 + h * 0.2, -d * 0.5),
      ),
    ],
    height: cy + R * 0.7 + h * 0.62,
    ports: {
      N: { p: v3(0, cy + R * 0.7 + h * 0.62, 0), n: v3(0, 1, 0) },
      W: { p: v3(-w * 0.45, cy, 0), n: v3(-1, 0, 0) },
      E: { p: v3(w * 0.45, cy, 0), n: v3(1, 0, 0) },
      S: { p: v3(0, cy + R * 0.7 + h * 0.2, -d * 0.51), n: v3(0, 0, -1) },
    },
    footprint: rectFoot(w, d),
  };
};
